/**
 * TanStack Query hooks for the pause change_request billing-delta preview (#784).
 * Plan (read-only billing-delta preview) and Process (recovery apply) — both
 * calling the generated ChangeRequestsService. Mirrors useCareDayRequest.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';

import { ChangeRequestsService } from '@/api/services/ChangeRequestsService';
import { ApiError } from '@/api';
import { workflowKeys } from './useWorkflows';

/**
 * Read-only billing-delta preview for a pause change_request item: the bounded
 * window, the create/extend/cancel sub-type, and the affected invoices. Surfaces a
 * toast on failure; the drawer renders the resolved data.
 */
export function usePlanPause() {
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (id: string) => ChangeRequestsService.getApiV1ChangeRequestsPausePlan(id),
    onError: () => {
      toast.error(
        i18n._({
          id: 'changeRequest.toast.planError',
          message: 'Failed to load the change preview',
        }),
      );
    },
  });
}

/**
 * Recovery apply for an approved pause item whose automatic execution failed.
 * Invalidates the inbox + the item detail. Surfaces the real per-apply outcome
 * (Process returns HTTP 200 even on a failed apply).
 */
export function useProcessPause() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      ChangeRequestsService.postApiV1ChangeRequestsPauseProcess(id),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.pendingCount() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(variables.id) });

      if (data?.status === 'error') {
        // pause is a contract-level billing change — reuse the generic (already
        // translated) apply-error message rather than the child-specific copy.
        toast.error(
          i18n._({ id: 'changeRequest.toast.applyError', message: 'Failed to apply the change request' }),
        );
      } else {
        toast.success(
          i18n._({ id: 'changeRequest.toast.applied', message: 'Change request applied' }),
        );
      }
    },
    onError: (error: Error) => {
      if (error instanceof ApiError && error.status === 409) {
        toast.error(
          i18n._({
            id: 'changeRequest.toast.applyConflict',
            message: 'This change request must be approved before it can be applied',
          }),
        );
        return;
      }
      toast.error(
        i18n._({
          id: 'changeRequest.toast.applyError',
          message: 'Failed to apply the change request',
        }),
      );
    },
  });
}
